import React from 'react';
import { motion } from 'framer-motion';
import { clayStaggerContainer, clayStaggerItem } from '../../constants/animations';
import { MachhaCharacter } from '../../lib/machhaAsset';
import styles from './EmptyState.module.css';

type CharacterState =
  | 'Confirmation' | 'Empty' | 'Error' | 'Failure' | 'Forbidden'
  | 'Loading' | 'Maintanance' | 'Milestone' | 'Not Found' | 'Offline'
  | 'Onboarding' | 'Rate Limit' | 'Success' | 'Warning' | 'Welcome';

interface EmptyStateProps {
  characterState?: CharacterState;
  /** Fallback emoji icon (only used when characterState is explicitly undefined) */
  icon?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
  /** Size of the character in px. Defaults to 160 */
  characterSize?: number;
}

export function EmptyState({
  characterState = 'Empty',
  icon,
  title,
  description,
  action,
  characterSize = 160,
}: EmptyStateProps) {
  const useFace = characterSize < 100;

  return (
    <motion.div
      className={styles.root}
      variants={clayStaggerContainer}
      initial="hidden"
      animate="visible"
    >
      {/* Outer node owns the one-shot entry animation; inner node owns the
          looping idle bob — splitting avoids the loop restarting on re-render. */}
      <motion.div
        className={styles.character}
        variants={{ hidden: { opacity: 0, y: 24, scale: 0.95 }, visible: { opacity: 1, y: 0, scale: 1 } }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <motion.div
          className={styles['character-bob']}
          animate={{ y: [0, -10, 0] }}
          transition={{ repeat: Infinity, duration: 3.5, ease: 'easeInOut' }}
        >
          {icon && characterState === undefined ? (
            <span style={{ fontSize: '3rem' }}>{icon}</span>
          ) : (
            <MachhaCharacter
              state={characterState}
              part={useFace ? 'face' : 'body'}
              size={characterSize}
            />
          )}
        </motion.div>
      </motion.div>

      <div className={styles.content}>
        <motion.h3 className={styles.title} variants={clayStaggerItem}>
          {title}
        </motion.h3>
        {description && (
          <motion.p className={styles.description} variants={clayStaggerItem}>
            {description}
          </motion.p>
        )}
        {action && (
          <motion.div variants={clayStaggerItem} className={styles.action}>
            {action}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
